import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Phone, Mail, MapPin, ArrowRight, CheckCircle, Upload, Sheet } from 'lucide-react';

export default function Inquiries({ currentUser }) {
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showSheetsDialog, setShowSheetsDialog] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [sheetsConfig, setSheetsConfig] = useState({
    spreadsheet_id: '',
    sheet_name: 'meta ads jamnagar',
    start_row: 2,
    api_key: ''
  });
  const [importing, setImporting] = useState(false);
  const [newInquiry, setNewInquiry] = useState({
    customer_name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    branch: currentUser?.branch || 'Rajkot',
    monthly_bill_amount: '',
    interested_capacity: '',
    notes: ''
  });

  useEffect(() => {
    fetchInquiries();
  }, [filter]);

  const fetchInquiries = async () => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/inquiries`;
      if (filter !== 'all') {
        url += `?status=${filter}`;
      }
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      const data = await response.json();
      setInquiries(data);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    }
  };

  const handleAddInquiry = async (e) => {
    e.preventDefault();
    
    // Optimistically add to UI
    const optimisticInquiry = {
      inquiry_id: `temp_${Date.now()}`,
      ...newInquiry,
      status: 'new',
      source: 'phone',
      created_at: new Date().toISOString(),
      ai_classified: false
    };
    
    setInquiries(prev => [optimisticInquiry, ...prev]);
    setShowAddDialog(false);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newInquiry,
          monthly_bill_amount: parseFloat(newInquiry.monthly_bill_amount) || null,
          source: 'phone'
        })
      });

      if (response.ok) {
        // Refresh to get the real inquiry with AI classification
        fetchInquiries();
        setNewInquiry({
          customer_name: '',
          phone: '',
          email: '',
          address: '',
          city: '',
          branch: currentUser?.branch || 'Rajkot',
          monthly_bill_amount: '',
          interested_capacity: '',
          notes: ''
        });
      } else {
        // Remove optimistic inquiry if failed
        setInquiries(prev => prev.filter(i => i.inquiry_id !== optimisticInquiry.inquiry_id));
      }
    } catch (error) {
      console.error('Error adding inquiry:', error);
      setInquiries(prev => prev.filter(i => i.inquiry_id !== optimisticInquiry.inquiry_id));
    }
  };

  const handleConvertToProject = async (inquiryId) => {
    if (!confirm('Convert this inquiry to a project?')) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inquiries/${inquiryId}/convert`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Inquiry converted! Project ID: ${data.project_id}`);
        fetchInquiries();
      }
    } catch (error) {
      console.error('Error converting inquiry:', error);
    }
  };

  const handleGoogleSheetsImport = async () => {
    if (!sheetsConfig.spreadsheet_id) {
      alert('Please enter Spreadsheet ID');
      return;
    }

    setImporting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sheets/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(sheetsConfig)
      });

      if (response.ok) {
        const data = await response.json();
        const errorMsg = data.errors && data.errors.length > 0 
          ? `\n\nErrors:\n${data.errors.join('\n')}`
          : '';
        alert(`Import successful! ✅\n\nImported: ${data.imported}\nSkipped (duplicates): ${data.skipped}\nTotal rows: ${data.total_rows}${errorMsg}`);
        setShowSheetsDialog(false);
        fetchInquiries();
      } else {
        const error = await response.json();
        alert(`Import failed ❌\n\n${error.detail || 'Unknown error'}\n\nMake sure:\n1. Sheet is public ("Anyone with link")\n2. Spreadsheet ID is correct\n3. Sheet name matches (default: "Form Responses 1")`);
      }
    } catch (error) {
      console.error('Error importing from sheets:', error);
      alert('Error importing from Google Sheets');
    } finally {
      setImporting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-orange-100 text-orange-800',
      'contacted': 'bg-blue-100 text-blue-800',
      'site_visit_scheduled': 'bg-purple-100 text-purple-800',
      'quotation_sent': 'bg-yellow-100 text-yellow-800',
      'converted': 'bg-green-100 text-green-800',
      'lost': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Inquiries</h1>
          <p className="text-muted-foreground mt-1">
            Manage customer inquiries from Google Forms and other sources
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showSheetsDialog} onOpenChange={setShowSheetsDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" disabled={!!currentUser?.branch}>
                <Sheet className="mr-2 h-4 w-4" />
                Import from Google Sheets
              </Button>
            </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Import from Google Sheets</DialogTitle>
                  <DialogDescription>
                    Import inquiries in bulk from your Google Form responses
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="spreadsheet_id">Spreadsheet ID or URL *</Label>
                    <Input
                      id="spreadsheet_id"
                      value={sheetsConfig.spreadsheet_id}
                      onChange={(e) => setSheetsConfig({...sheetsConfig, spreadsheet_id: e.target.value})}
                      placeholder="1ABC...XYZ or full URL"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Paste full URL or just the ID between /d/ and /edit
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="sheet_name">Sheet Name</Label>
                    <Input
                      id="sheet_name"
                      value={sheetsConfig.sheet_name}
                      onChange={(e) => setSheetsConfig({...sheetsConfig, sheet_name: e.target.value})}
                      placeholder="Form Responses 1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="start_row">Start Row (skip header)</Label>
                    <Input
                      id="start_row"
                      type="number"
                      value={sheetsConfig.start_row}
                      onChange={(e) => setSheetsConfig({...sheetsConfig, start_row: parseInt(e.target.value) || 2})}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Usually 2 (to skip header row)
                    </p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-md text-sm">
                    <p className="font-semibold mb-1">📋 Setup Steps:</p>
                    <ol className="list-decimal list-inside space-y-1 text-xs">
                      <li>Open your Google Sheet</li>
                      <li>Click Share → "Anyone with link" → Viewer</li>
                      <li>Copy the Spreadsheet ID from URL</li>
                      <li>URL format: docs.google.com/spreadsheets/d/<strong className="text-blue-600">SPREADSHEET_ID</strong>/edit</li>
                      <li>Paste the ID above and click Import</li>
                    </ol>
                    <p className="text-xs mt-2 text-blue-700">✨ The system auto-detects columns and AI-classifies each inquiry!</p>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowSheetsDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleGoogleSheetsImport} disabled={importing}>
                      {importing ? 'Importing...' : 'Import Inquiries'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
          </Dialog>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Inquiry
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add New Inquiry</DialogTitle>
                <DialogDescription>
                  Add a new customer inquiry manually
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddInquiry} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="customer_name">Customer Name *</Label>
                    <Input
                      id="customer_name"
                      value={newInquiry.customer_name}
                      onChange={(e) => setNewInquiry({...newInquiry, customer_name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      value={newInquiry.phone}
                      onChange={(e) => setNewInquiry({...newInquiry, phone: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newInquiry.email}
                      onChange={(e) => setNewInquiry({...newInquiry, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={newInquiry.city}
                      onChange={(e) => setNewInquiry({...newInquiry, city: e.target.value})}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="address">Address *</Label>
                    <Input
                      id="address"
                      value={newInquiry.address}
                      onChange={(e) => setNewInquiry({...newInquiry, address: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="branch">Branch *</Label>
                    <select
                      id="branch"
                      value={newInquiry.branch}
                      onChange={(e) => setNewInquiry({...newInquiry, branch: e.target.value})}
                      className="w-full border rounded-md p-2"
                      disabled={!!currentUser?.branch}
                    >
                      <option value="Rajkot">Rajkot</option>
                      <option value="Jamnagar">Jamnagar</option>
                      <option value="Porbandar">Porbandar</option>
                      <option value="Junagadh">Junagadh</option>
                      <option value="Morbi">Morbi</option>
                      <option value="Khambhaliya">Khambhaliya</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="monthly_bill">Monthly Bill Amount (₹)</Label>
                    <Input
                      id="monthly_bill"
                      type="number"
                      value={newInquiry.monthly_bill_amount}
                      onChange={(e) => setNewInquiry({...newInquiry, monthly_bill_amount: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="capacity">Interested Capacity</Label>
                    <select
                      id="capacity"
                      value={newInquiry.interested_capacity}
                      onChange={(e) => setNewInquiry({...newInquiry, interested_capacity: e.target.value})}
                      className="w-full border rounded-md p-2"
                    >
                      <option value="">Select</option>
                      <option value="3KW">3KW</option>
                      <option value="5KW">5KW</option>
                      <option value="10KW">10KW</option>
                      <option value="15KW">15KW</option>
                      <option value="20KW+">20KW+</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="notes">Notes</Label>
                    <textarea
                      id="notes"
                      value={newInquiry.notes}
                      onChange={(e) => setNewInquiry({...newInquiry, notes: e.target.value})}
                      className="w-full border rounded-md p-2"
                      rows="3"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">Add Inquiry</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['all', 'new', 'contacted', 'site_visit_scheduled', 'quotation_sent', 'converted', 'lost'].map(status => (
          <Button
            key={status}
            variant={filter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(status)}
          >
            {status === 'all' ? 'All' : status.replace(/_/g, ' ')}
          </Button>
        ))}
      </div>

      {/* Inquiries List */}
      <div className="grid gap-4">
        {inquiries.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <p className="text-muted-foreground">No inquiries found</p>
            </CardContent>
          </Card>
        ) : (
          inquiries.map((inquiry) => (
            <Card key={inquiry.inquiry_id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{inquiry.customer_name}</h3>
                      <Badge className={getStatusColor(inquiry.status)}>
                        {inquiry.status.replace(/_/g, ' ')}
                      </Badge>
                      <Badge variant="outline">{inquiry.branch}</Badge>
                      {inquiry.priority && (
                        <Badge 
                          className={
                            inquiry.priority === 'hot' ? 'bg-red-100 text-red-800' :
                            inquiry.priority === 'warm' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }
                        >
                          {inquiry.priority === 'hot' ? '🔥' : inquiry.priority === 'warm' ? '⚡' : '❄️'} {inquiry.priority.toUpperCase()}
                        </Badge>
                      )}
                      {inquiry.ai_classified && (
                        <Badge variant="outline" className="text-xs bg-purple-50">
                          ✨ AI
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        {inquiry.phone}
                      </div>
                      {inquiry.email && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Mail className="h-4 w-4" />
                          {inquiry.email}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        {inquiry.city}
                      </div>
                      {inquiry.interested_capacity && (
                        <div className="text-muted-foreground">
                          Interested: {inquiry.interested_capacity}
                        </div>
                      )}
                    </div>
                    {inquiry.notes && (
                      <p className="text-sm text-muted-foreground mt-2">{inquiry.notes}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {inquiry.status !== 'converted' && (
                      <Button
                        size="sm"
                        onClick={() => handleConvertToProject(inquiry.inquiry_id)}
                      >
                        <ArrowRight className="mr-2 h-4 w-4" />
                        Convert to Project
                      </Button>
                    )}
                    {inquiry.status === 'converted' && (
                      <Button size="sm" variant="outline" disabled>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Converted
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
